/* eslint-disable react/require-default-props */
/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { get } from 'lodash';
import { getWorkspaceId } from 'features/project/projectSlice';
import { MiniLoader } from '@pwc/pwc-deals-ui';
import 'tableau-api';
import { Box } from '@mui/material';
import { TableauData } from 'features/analyses/tableauService';
import {
  convertToSingleTabEmbedUrl,
  getTabNameForEmbedUrl,
  resizeTableau,
} from './tableauUtils';
import SyncedInfo from 'common/components/Autosync/SyncedInfo';
import './TableauPreview.scss';
import TableauErrorMessage from './TableauErrorMessage/TableauErrorMessage';
import { useLiveReportPreview } from '@pwc/pwc-deals-report-ui/build/components/ReportPreview/provider';
import {
  getTableauAutoSyncUrl,
  getTableauReportDetail,
} from 'features/analyses/tableauService';
import { useShouldDisplayReportPreview } from '../RibbonPanel/useShouldDisplayReportPreview';
import {
  getReportPreviewPropsSelector,
  setReportPreviewProps,
  updateLibraryItem,
} from '../../../features/analyses/libraryItemsSlice';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';

// eslint-disable-next-line
const hasJsonStructure = (str: any) => {
  if (typeof str !== 'string') return false;
  try {
    const result = JSON.parse(str); // eslint-disable-line
    const type = Object.prototype.toString.call(result);
    return type === '[object Object]' || type === '[object Array]';
  } catch (err) {
    return false;
  }
};

declare global {
  interface Window {
    tableau: any; // eslint-disable-line
  }
}

type Props = {
  report: any; // eslint-disable-line
  setIsReportRendered: any; // eslint-disable-line
  setTableauInstance: any; // eslint-disable-line
  hideTabs?: boolean;
  showAutoSyncLink?: boolean;
  height?: number;
};

// eslint-disable-next-line
export const TableauPreview = ({
  report,
  setIsReportRendered,
  setTableauInstance,
  hideTabs = false,
  showAutoSyncLink = true,
  height,
}: Props) => {
  const dispatch = useDispatch();
  const tableauRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);
  const hideTabsRef = useRef(hideTabs);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

  const autoSyncUrlRef = useRef<unknown>('');
  const dimensionsRef = useRef({
    height: 650,
    width: '100%',
  });
  const vizRef = useRef<any>();
  const shouldDisplayReportPreview = useShouldDisplayReportPreview();
  // @ts-ignore
  const { isDisplayed } = useSelector(getReportPreviewPropsSelector) || {};

  const [loading, setLoading] = useState(true);
  const workspaceId = useSelector(getWorkspaceId); // eslint-disable-line
  const { projectId } = useParams<any>(); // eslint-disable-line
  const [isError, setError] = useState(false);
  const [isApiError, setIsApiError] = useState(false);
  const reportPreviewData = useLiveReportPreview();
  const image: { placeholderImage?: string } = reportPreviewData;

  let containerProps: { position: string; height?: string } = {
    position: 'relative',
  };

  if (height) {
    containerProps = {
      ...containerProps,
      height: `${height}px`,
    };
  }

  useEffect(() => {
    const loadSnapshot = async () => {
      if (!report) return;

      const { id } = report;

      if (shouldDisplayReportPreview) {
        dispatch &&
          dispatch(updateLibraryItem({ id, item: image.placeholderImage }));
        if (dispatch) {
          dispatch(
            setReportPreviewProps({
              isDirty: true,
            })
          );
        }
      }
    };
    loadSnapshot().then();
  }, [shouldDisplayReportPreview, image.placeholderImage, (report || {}).id]);

  useEffect(() => {
    if (!workspaceId) {
      return;
    }
    const loadTableau = async () => {
      setError(false);
      const { embedUrl, sheetUrl } = report; // eslint-disable-line
      let embedUrlFromApi = '';
      let tabName = '';

      try {
        const autoSyncData = await getTableauAutoSyncUrl(
          projectId,
          workspaceId,
          report.reportId // eslint-disable-line
        );
        autoSyncUrlRef.current = autoSyncData.embedUrl;
        if (!embedUrl && !sheetUrl) {
          const data = await getTableauReportDetail(
            projectId,
            workspaceId,
            report.reportId // eslint-disable-line
          );
          if (!mountedRef.current) {
            return;
          }
          if (data === false) {
            setIsReportRendered(false);
            setIsApiError(true);
            return;
          }
          // eslint-disable-next-line
          tabName =
            report.tabName || // eslint-disable-line
            (hasJsonStructure(report.data) && // eslint-disable-line
              get(JSON.parse(report.data), 'tabName', '')) || // eslint-disable-line
            (hasJsonStructure(report.data) && // eslint-disable-line
              get(JSON.parse(report.data), 'TabName', '')); // eslint-disable-line
          if (tabName) {
            const tabNameForEmbedUrl = getTabNameForEmbedUrl(tabName);
            // eslint-disable-next-line
            embedUrlFromApi = convertToSingleTabEmbedUrl(
              (data as TableauData).embedUrl,
              tabNameForEmbedUrl
            );
          } else {
            embedUrlFromApi = (data as TableauData).embedUrl;
            hideTabsRef.current = false;
          }
        }
      } catch (error) {
        setLoading(false);
        setError(true);
        return;
      }

      const options = {
        hideTabs: hideTabsRef.current,
        hideToolbar: true,
        width: dimensionsRef.current.width,
        height: containerProps.height || dimensionsRef.current.height,
        onFirstVizSizeKnown: () => {
          setLoading(false);
        },
        onFirstInteractive: () => {
          setLoading(false);
          if (setIsReportRendered) {
            setIsReportRendered(true); // eslint-disable-line
          }
          if (tableauRef.current) {
            tableauRef.current.style.overflow = 'hidden';
            resizeTableau(vizRef, tableauRef, dimensionsRef, height);
          }
        },
      };

      // @ts-ignore-next-line
      const instance = new window.tableau.Viz( // eslint-disable-line
        tableauRef.current,
        sheetUrl || embedUrl || embedUrlFromApi,
        options
      );
      vizRef.current = instance;
      if (setTableauInstance) {
        setTableauInstance(instance); // eslint-disable-line
      }
    };
    if (tableauRef.current && report && !vizRef.current && !isError) {
      loadTableau(); // eslint-disable-line
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableauRef,
    report,
    setIsReportRendered,
    vizRef.current,
    setTableauInstance,
    hideTabs,
    workspaceId,
  ]);

  useEffect(() => {
    window.addEventListener(
      'resize',
      resizeTableau.bind(this, vizRef, tableauRef, dimensionsRef, height)
    );

    if (vizRef.current) {
      vizRef.current.addEventListener(
        'tabswitch',
        resizeTableau.bind(this, vizRef, tableauRef, dimensionsRef)
      );
    }

    return () => {
      window.removeEventListener('resize', resizeTableau);
      if (vizRef.current) {
        vizRef.current.removeEventListener('tabswitch');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vizRef.current, tableauRef.current, dimensionsRef.current]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  if (isApiError) {
    return (
      <Box
        sx={{
          ...containerProps,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <TableauErrorMessage title="Unable to load file" isShared={false} />
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={
          isError
            ? {
                ...containerProps,
                height: '100%',
              }
            : containerProps
        }
      >
        {isError && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <TableauErrorMessage title="Unable to load file" isShared={false} />
          </Box>
        )}
        {loading && !isError ? (
          <Box
            sx={{
              position: 'absolute',
              top: 'calc(50% - 20px)',
              left: 'calc(50% - 20px)',
            }}
          >
            <MiniLoader />
          </Box>
        ) : null}
        {!isError && (
          <div
            data-testid="tableau"
            ref={tableauRef}
            className={
              !height
                ? 'rich-text-library-item-edit tableau-preview'
                : 'rich-text-library-item-edit'
            }
          />
        )}
      </Box>
      {autoSyncUrlRef.current && showAutoSyncLink && (
        <div className="sync-wrapper">
          <SyncedInfo
            isPwcUser={isPwcUser}
            projectId={projectId as string}
            isTableau
            tableauDisplayName={report.name as string}
            tableauEmbedUrl={autoSyncUrlRef.current as string}
          />
        </div>
      )}
    </>
  );
};
